
import { useCallback, useEffect, useRef } from "react";
import type { FeaturesProps } from "./interface";

const groupEntriesByRow = (entries: ResizeObserverEntry[]) => {
  const groups: Record<string, HTMLDivElement[]> = {};

  for (const entry of entries) {
    const target = entry.target;
    const top = target.getBoundingClientRect().top;
    const height = target.getBoundingClientRect().height;
  
    const position = `${top},${height}`;

    if (!groups[position]) {
      groups[position] = [];
    }

    groups[position].push(entry.target as HTMLDivElement);
  }

  return groups;
}

const cleanUpEntries = (entries: ResizeObserverEntry[]) => {
  for (const entry of entries) {
    const target = entry.target;
    const description = target.querySelector('.feature-description');

    if ( description ) {
      (description as HTMLDivElement).style.removeProperty( 'height' );
    }
  }
}

const alignDescriptionForGroups = (groups: Record<string, HTMLDivElement[]>) => {
  Object.keys(groups).forEach(position => {
    const elements = groups[position];
    const maxDescriptionHeight = elements.reduce((acc, curr) => Math.max(acc, (curr.querySelector('.feature-description') as HTMLDivElement)?.offsetHeight), 0);

    elements.forEach(element => {
      const description = element.querySelector('.feature-description');

      if ( description ) {
        (description as HTMLDivElement).style.height = `${ maxDescriptionHeight }px`;
      }
    })
  })
}

export const useFeatureResizeCallback = (features: FeaturesProps['data']['feature']) => {

    // Refs for the features
    const measuredElementsRefs = useRef<Record<string, HTMLDivElement | null>>({});

    // Callback ref function
    const setRef = useCallback((id: string) => (element: HTMLDivElement | null) => {
      measuredElementsRefs.current[id] = element;
    }, []);

    // Function to group features based on their positions
    useEffect(() => {
      const elements = measuredElementsRefs.current;
  
      const resizeObserver = new ResizeObserver((entries) => {
        cleanUpEntries(entries)
        const groups = groupEntriesByRow(entries);
        alignDescriptionForGroups( groups )
      });
  
      features.forEach(feature => {
        const ref = measuredElementsRefs.current[feature.id];
        ref && resizeObserver.observe(ref);
      });
  
      return () => {
        features.forEach(feature => {
          const ref = elements[feature.id];
  
          if (!ref) return;
  
          resizeObserver.unobserve(ref);
        })
      }
    }, [features]);

    return setRef;
}
